/**
 * The password policy, in one place.
 *
 * Two screens ask somebody to choose a password — finishing a registration and accepting an
 * invitation — and they are used by different people at different times, so a rule written twice
 * would eventually be written two ways.
 *
 * These mirror the server's Identity options (Invetrak.Infrastructure.Identity.
 * AuthServiceCollectionExtensions: RequireDigit, RequireLowercase, RequireUppercase,
 * RequireNonAlphanumeric, RequiredLength). Disagreeing with them means a password the meter calls
 * strong and the server then rejects, which reads as a broken form rather than a rule.
 *
 * It is a mirror, not the source: the server decides. The screens surface whatever it says when it
 * refuses, rather than assuming this file was right.
 */

/** The server's default minimum. Its configured length can be raised above this, never below. */
export const PASSWORD_MIN_LENGTH = 12;

export interface PasswordStrength {
  /** How many of the four requirements are met. */
  score: 0 | 1 | 2 | 3 | 4;
  hasMinLen: boolean;
  hasUpperLower: boolean;
  hasNumber: boolean;
  hasSymbol: boolean;
}

export function ratePassword(password: string): PasswordStrength {
  const hasMinLen = password.length >= PASSWORD_MIN_LENGTH;
  const hasUpperLower = /[a-z]/.test(password) && /[A-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSymbol = /[^A-Za-z0-9]/.test(password);
  const passed = [hasMinLen, hasUpperLower, hasNumber, hasSymbol].filter(Boolean).length;
  return {
    score: passed as PasswordStrength['score'],
    hasMinLen,
    hasUpperLower,
    hasNumber,
    hasSymbol,
  };
}
